import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { useFloating } from '@platform/ui';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import type { IControlProps } from '../IControlProps';

/**
 * Довгий текст у клітинці таблиці (UIUX/field-types/long-text «Inline edit opens a larger editor»): рядок 30px з першим рядком
 * тексту; клік або Enter відкриває плаваючий редактор 360×160 з автофокусом; Ctrl/⌘+Enter, клік поза ним чи Esc закривають.
 */
export function LongTextPopover({ value, onChange, readOnly, id, field, rt }: IControlProps<string>) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const pos = useFloating(open, anchor, panel, () => setOpen(false), { estimate: 190, minWidth: 360 });
  const text = typeof value === 'string' ? value : '';
  const label = strings.field(rt.key, field.key, field.label);
  return (
    <>
      <button ref={anchor} id={id} type="button" className={cx('pk', 'pk--inline', 'fc-longline', readOnly && 'is-ro')} aria-label={label} aria-expanded={open}
        title={text || undefined} onClick={() => !readOnly && setOpen((o) => !o)}>
        <span className={cx('pk__text', !text && 'is-empty')}>{text ? text.split('\n')[0] : t('fieldControls.notSet')}</span>
      </button>
      {open && pos && createPortal(
        <div ref={panel} className="pk-list fc-longpanel" style={{ left: pos.left, top: pos.top, bottom: pos.bottom, minWidth: pos.minWidth }}>
          <textarea className="control fc-area" autoFocus aria-label={label} value={text} onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); setOpen(false); anchor.current?.focus(); } }} />
        </div>,
        document.body,
      )}
    </>
  );
}
